'use client';

import { type ReactNode, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconButton } from './icon-button';

const SIZE = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof SIZE;
  /** Hide the corner close button (keep Escape and the footer's own Cancel). */
  hideClose?: boolean;
  className?: string;
  /** Element id for aria-labelledby overrides; defaults are generated. */
  id?: string;
}

/**
 * Dialog on the native `<dialog>` element (DESIGN.md §6): `showModal()` gives
 * the top layer, focus trapping, Escape handling and focus return for free.
 * Backdrop click closes; body scroll is locked while open.
 */
export function Dialog({ open, onClose, title, description, children, footer, size = 'md', hideClose, className, id }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = `${id ?? 'dialog'}-title`;
  const descId = `${id ?? 'dialog'}-desc`;

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className={cn(
        'm-auto w-[calc(100%-2rem)] rounded-panel border border-line bg-surface p-0 text-ink',
        'shadow-[0_24px_64px_rgb(0_0_0/0.4),inset_0_1px_0_rgb(255_255_255/0.04)]',
        'backdrop:bg-black/60 backdrop:backdrop-blur-[2px]',
        'open:animate-[dialog-in_180ms_cubic-bezier(0.2,0,0,1)]',
        SIZE[size],
        className
      )}
    >
      {open ? (
        <div className="flex max-h-[85dvh] flex-col">
          <div className="flex items-start justify-between gap-4 px-5 pt-5">
            <div className="min-w-0">
              <h2 id={titleId} className="text-lg font-semibold tracking-tight text-ink">
                {title}
              </h2>
              {description ? (
                <p id={descId} className="mt-1 text-[13px] text-ink-2">
                  {description}
                </p>
              ) : null}
            </div>
            {!hideClose ? (
              <IconButton label="Close" size="sm" onClick={onClose} className="-me-2 -mt-1">
                <X />
              </IconButton>
            ) : null}
          </div>
          {children ? <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm">{children}</div> : <div className="h-4" />}
          {footer ? <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}
